import React, { useState, useEffect } from 'react';
import Preloader from '../../components/common/Preloader';
import { useParams, useNavigate } from 'react-router-dom';
import { getEmployeeById, getPayrollStatus, terminateEmployee, reactivateEmployee, getEmployeeTimeEntries, promoteEmployee } from '../../services/adminService';
import { showConfirm, showSuccess, showError, showToast } from '../../utils/sweetAlert';
import { formatCurrency } from '../../utils/format';
import TimeGraph from '../../components/common/TimeGraph';
import SalarySlipModal from '../../components/admin/SalarySlipModal';
import PromotionModal from '../../components/admin/PromotionModal';
import AttendanceModal from '../../components/admin/AttendanceModal';
import EditEmployeeModal from '../../components/admin/EditEmployeeModal';
import { connectSocket, disconnectSocket } from '../../services/socket';
import './AdminEmployeeView.css';

const AdminEmployeeView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [employee, setEmployee] = useState(null);
    const [loading, setLoading] = useState(true);
    const [, setError] = useState('');

    // Payroll State
    const [payrollStatus, setPayrollStatus] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const [showSlipModal, setShowSlipModal] = useState(false);

    // Graph State
    const [graphEntries, setGraphEntries] = useState([]);
    const [graphRange, setGraphRange] = useState({ start: new Date(), end: new Date() });

    // Actions State
    const [showTerminate, setShowTerminate] = useState(false);
    const [terminationReason, setTerminationReason] = useState('');
    const [showPromotionModal, setShowPromotionModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAttendanceModal, setShowAttendanceModal] = useState(false);
    const [updating, setUpdating] = useState(false);

    useEffect(() => {
        loadData();

        // Connect to socket for live updates
        const socket = connectSocket();
        if (socket) {
            socket.on('notification', (data) => {
                if (data.type === 'attendanceUpdated' && String(data.payload.employeeId) === String(id)) {
                    console.log('Attendance updated, refreshing graph...');
                    loadData();
                }
            });
        }

        return () => {
            disconnectSocket();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, currentMonth, currentYear]);

    const loadData = async () => {
        try {
            setLoading(true);
            // Load Employee
            const empData = await getEmployeeById(id);
            setEmployee(empData);

            // Load Payroll
            const payrollData = await getPayrollStatus(currentMonth, currentYear);
            // Filter for this employee
            const status = payrollData.find(p => p.employee === id);
            setPayrollStatus(status);

            // Load Graph
            const { start, end } = calculateDateRange();
            setGraphRange({ start, end });
            const entries = await getEmployeeTimeEntries(id, currentMonth, currentYear);
            setGraphEntries(entries);

        } catch (err) {
            setError(err.message || 'Failed to load employee data');
        } finally {
            setLoading(false);
        }
    };

    const calculateDateRange = () => {
        const start = new Date(currentYear, currentMonth - 1, 1);
        const end = new Date(currentYear, currentMonth, 0, 23, 59, 59);
        return { start, end };
    };

    const handlePaySalary = () => {
        navigate(`/admin/employees/${employee._id}/pay-salary?month=${currentMonth}&year=${currentYear}`);
    };

    const handleTerminate = async () => {
        if (!terminationReason.trim()) return showError('Error', 'Reason is required for termination');

        const confirmed = await showConfirm(
            'Terminate Employee?',
            'Are you sure you want to terminate this employee? This action cannot be undone easily.',
            'warning',
            'Yes, Terminate'
        );
        if (!confirmed) return;

        setUpdating(true);
        try {
            await terminateEmployee(employee._id, terminationReason);
            setShowTerminate(false);
            loadData();
            showSuccess('Terminated', 'Employee has been terminated successfully.');
        } catch (err) {
            showError('Termination Failed', err.message);
        } finally {
            setUpdating(false);
        }
    };

    const handleReactivate = async () => {
        const confirmed = await showConfirm(
            'Reactivate Employee?',
            'Are you sure you want to reactivate this employee?',
            'question',
            'Yes, Reactivate'
        );
        if (!confirmed) return;

        setUpdating(true);
        try {
            await reactivateEmployee(employee._id);
            loadData();
            showSuccess('Reactivated', 'Employee has been reactivated successfully.');
        } catch (err) {
            showError('Reactivation Failed', err.message);
        } finally {
            setUpdating(false);
        }
    };

    const handlePromote = async (employeeId, designation, remarks) => {
        try {
            await promoteEmployee(employeeId, designation, remarks);
            setShowPromotionModal(false);
            loadData();
            showToast('Employee promoted successfully!', 'success');
        } catch (err) {
            showError('Promotion Failed', err.message);
        }
    };

    if (loading && !employee) return <Preloader />;
    if (!employee && !loading) return <div className="error-state">Employee not found</div>;

    return (
        <div className="admin-employee-view">
            {loading && <div className="loading-overlay-top">Updating...</div>}
            <div className="view-header">
                <button onClick={() => navigate('/admin/employees')} className="btn-back">
                    ← Back to List
                </button>
                <div className="header-content">
                    <h1>{employee.name}</h1>
                    <span className={`status-badge ${employee.status}`}>{employee.status}</span>
                </div>
            </div>

            <div className="dashboard-grid">
                {/* Card 1: Personal Info */}
                <div className="dashboard-card info-card">
                    <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '40%', alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: '0.5rem', marginBottom: '1rem' }}>
                        <h2 style={{ borderBottom: 'none', paddingBottom: '0', marginBottom: '0' }}>Personal Information</h2>
                        <button className="btn-primary" style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem', width: 'auto', whiteSpace: 'nowrap', flexShrink: 0 }} onClick={() => setShowEditModal(true)}>
                            ✏️ Edit Details
                        </button>
                    </div>
                    <div className="info-list">
                        <div className="info-item profile-photo-section" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '1.5rem', width: '100%', gridColumn: '1 / -1' }}>
                            <div style={{
                                width: '90px', height: '90px', borderRadius: '50%', backgroundColor: '#e2e8f0',
                                marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                overflow: 'hidden', border: '3px solid #cbd5e1', position: 'relative'
                            }}>
                                {employee.profilePhoto ? (
                                    <img src={employee.profilePhoto} alt={employee.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                    <span style={{ fontSize: '3rem', color: '#94a3b8' }}>👤</span>
                                )}
                            </div>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <label style={{ cursor: 'pointer', color: '#fff', backgroundColor: '#3b82f6', padding: '0.4rem 0.8rem', borderRadius: '0.375rem', fontSize: '0.75rem', fontWeight: '600', transition: 'background-color 0.2s' }}>
                                    {updating ? 'Uploading...' : 'Update Photo'}
                                    <input 
                                        type="file" 
                                        accept="image/jpeg, image/png, image/webp" 
                                        style={{ display: 'none' }} 
                                        onChange={async (e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                if (file.size > 2 * 1024 * 1024) return showError('Error', 'Photo must be less than 2MB');
                                                setUpdating(true);
                                                const reader = new FileReader();
                                                reader.onloadend = async () => {
                                                    try {
                                                        const { updateEmployeePhoto } = await import('../../services/adminService');
                                                        await updateEmployeePhoto(employee._id, reader.result);
                                                        loadData();
                                                        showToast('Photo updated successfully!', 'success');
                                                    } catch (err) {
                                                        showError('Update Failed', err.message);
                                                    } finally {
                                                        setUpdating(false);
                                                    }
                                                };
                                                reader.readAsDataURL(file);
                                            }
                                        }}
                                        disabled={updating}
                                    />
                                </label>
                                {employee.profilePhoto && (
                                    <button 
                                        onClick={async () => {
                                            if (await showConfirm('Remove Photo?', 'Are you sure you want to remove this photo?')) {
                                                setUpdating(true);
                                                try {
                                                    const { updateEmployeePhoto } = await import('../../services/adminService');
                                                    await updateEmployeePhoto(employee._id, '');
                                                    loadData();
                                                    showToast('Photo removed successfully!', 'success');
                                                } catch (err) {
                                                    showError('Remove Failed', err.message);
                                                } finally {
                                                    setUpdating(false);
                                                }
                                            }
                                        }}
                                        disabled={updating}
                                        style={{ color: '#ef4444', backgroundColor: '#fee2e2', padding: '0.4rem 0.8rem', borderRadius: '0.375rem', fontSize: '0.75rem', fontWeight: '600', border: 'none', cursor: 'pointer', transition: 'background-color 0.2s' }}
                                    >
                                        Remove Photo
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="info-item">
                            <span className="label">Employee ID</span>
                            <span className="value">{employee.employeeId || employee._id.substring(0, 8).toUpperCase()}</span>
                        </div>
                        <div className="info-item">
                            <span className="label">Email</span>
                            <span className="value">{employee.email}</span>
                        </div>
                        <div className="info-item">
                            <span className="label">Designation</span>
                            <span className="value">{employee.designation || 'N/A'}</span>
                        </div>
                        <div className="info-item">
                            <span className="label">Department</span>
                            <span className="value">{employee.department || 'General'}</span>
                        </div>
                        <div className="info-item">
                            <span className="label">Employee Type</span>
                            <span className="value">{employee.employeeType || 'Full Time'}</span>
                        </div>
                        <div className="info-item">
                            <span className="label">Date of Joining</span>
                            <span className="value">{employee.dateOfJoining ? new Date(employee.dateOfJoining).toLocaleDateString('en-GB').replace(/\//g, '.') : 'N/A'}</span>
                        </div>
                        <div className="info-item">
                            <span className="label">Salary</span>
                            <span className="value">{formatCurrency(employee.salary)}</span>
                        </div>
                        {employee.status === 'terminated' && (
                            <div className="info-item error">
                                <span className="label">Terminated</span>
                                <span className="value">{employee.terminationReason}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Card 2: Actions & Payroll */}
                <div className="dashboard-card actions-card">
                    <h2>Actions & Payroll</h2>

                    <div className="month-selector">
                        <label>Pay Period</label>
                        <div className="month-inputs">
                            <select value={currentMonth} onChange={(e) => setCurrentMonth(parseInt(e.target.value))}>
                                {Array.from({ length: 12 }, (_, i) => (
                                    <option key={i} value={i + 1}>{new Date(0, i).toLocaleString('default', { month: 'short' })}</option>
                                ))}
                            </select>
                            <select value={currentYear} onChange={(e) => setCurrentYear(parseInt(e.target.value))}>
                                {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                        </div>
                    </div>

                    <div className="action-buttons" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
                        {(payrollStatus && payrollStatus.isSent) ? (
                            <>
                                <button className="btn-action btn-slip" onClick={() => setShowSlipModal(true)}>
                                    📄 View Payslip
                                </button>
                                <button
                                    className="btn-action btn-pay"
                                    onClick={() => navigate(`/admin/employees/${employee._id}/pay-salary?month=${currentMonth}&year=${currentYear}&edit=true`)}
                                    style={{ backgroundColor: '#f59e0b', color: '#fff' }}
                                    disabled={employee.status !== 'active'}
                                >
                                    ✏️ Edit Salary
                                </button>
                            </>
                        ) : (
                            <button
                                className="btn-action btn-pay"
                                onClick={handlePaySalary}
                                disabled={employee.status !== 'active'}
                            >
                                💰 Pay Salary
                            </button>
                        )}

                        <button className="btn-action btn-promote" onClick={() => setShowPromotionModal(true)}>
                            ⭐ Promote
                        </button>

                        {employee.status === 'active' ? (
                            <button className="btn-action btn-terminate" onClick={() => setShowTerminate(true)}>
                                ⚠️ Terminate
                            </button>
                        ) : (
                            <button className="btn-action btn-terminate" style={{backgroundColor: '#22c55e', color: 'white'}} onClick={handleReactivate}>
                                🔄 Reactivate
                            </button>
                        )}

                        <button className="btn-action btn-attendance" style={{ backgroundColor: '#6366f1', color: 'white' }} onClick={() => setShowAttendanceModal(true)}>
                            📅 Attendance
                        </button>
                    </div>

                    {showTerminate && (
                        <div className="terminate-box">
                            <input
                                type="text"
                                placeholder="Enter termination reason..."
                                value={terminationReason}
                                onChange={(e) => setTerminationReason(e.target.value)}
                            />
                            <div className="t-actions">
                                <button onClick={handleTerminate} disabled={updating} className="btn-danger-sm">Confirm Terminate</button>
                                <button onClick={() => setShowTerminate(false)} className="btn-sec-sm">Cancel</button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Card 3: Attendance Graph */}
                <div className="dashboard-card graph-card-large">
                    <h2>Attendance Overview</h2>
                    <TimeGraph
                        entries={graphEntries}
                        startDate={graphRange.start}
                        endDate={graphRange.end}
                    />
                </div>
            </div>

            {showSlipModal && payrollStatus && (
                <SalarySlipModal
                    employee={employee}
                    payroll={{
                        ...payrollStatus,
                        month: currentMonth,
                        year: currentYear
                    }}
                    onClose={() => setShowSlipModal(false)}
                />
            )}

            {showPromotionModal && (
                <PromotionModal
                    employee={employee}
                    onClose={() => setShowPromotionModal(false)}
                    onPromote={handlePromote}
                />
            )}

            {showAttendanceModal && (
                <AttendanceModal
                    employee={employee}
                    month={currentMonth}
                    setMonth={setCurrentMonth}
                    year={currentYear}
                    setYear={setCurrentYear}
                    onClose={() => setShowAttendanceModal(false)}
                    onUpdate={loadData}
                />
            )}

            {showEditModal && (
                <EditEmployeeModal
                    employee={employee}
                    onClose={() => setShowEditModal(false)}
                    onSuccess={() => {
                        setShowEditModal(false);
                        loadData(); // Reload employee data after edit
                    }}
                />
            )}
        </div>
    );
};

export default AdminEmployeeView;
